import { APCAcontrast, alphaBlend, fontLookupAPCA, sRGBtoY } from './apca';
import { convertToRGBA } from './convertColors';
import * as Utils from '../utils/utils';

export function memoize(fn, keyResolver) {
  const cache = new Map();
  const memoized = (...args) => {
    const key = keyResolver ? keyResolver(...args) : JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };

  memoized.clear = () => {
    cache.clear();
  };

  return memoized;
}

/* Clears all memoized caches and the DOM background cache. */
export function resetContrastCaches() {
  clearBackgroundCache();
  if (getLuminance.clear) getLuminance.clear();
  if (getAPCAValue.clear) getAPCAValue.clear();
  if (calculateContrast.clear) calculateContrast.clear();
  if (suggestColorWCAG.clear) suggestColorWCAG.clear();
  if (suggestColorAPCA.clear) suggestColorAPCA.clear();
  if (extractColorFromString.clear) extractColorFromString.clear();
}

/**
 * Normalizes a given font weight to a numeric value. Maps keywords to their numeric equivalents.
 * @param {string|number} weight - The font weight, either as a number or a keyword.
 * @returns {number} - The numeric font weight.
 */
export function normalizeFontWeight(weight) {
  const numericWeight = parseInt(weight, 10);
  if (!Number.isNaN(numericWeight)) {
    return numericWeight;
  }
  const weightMap = {
    lighter: 100,
    normal: 400,
    bold: 700,
    bolder: 900,
  };
  return weightMap[weight] || 400;
}

// Initialize the WeakMap outside the function to persist the cache across calls.
let backgroundCache = new WeakMap();

/**
 * Retrieves the background colour of an element by traversing up the DOM tree,
 * accounting for slotted content, shadow DOM boundaries, and utilizing a WeakMap cache.
 * @param {HTMLElement} $el - The DOM element from which to start searching for the background.
 * @param {Boolean} shadowDetection - Whether to traverse shadow DOM.
 * @returns {string|Array|Object} - The background color in RGBA format, or object if image.
 */
export function getBackground($el, shadowDetection) {
  // 1. Check if we've already calculated the background for this exact element
  if (backgroundCache.has($el)) {
    return backgroundCache.get($el);
  }

  // Helper to traverse the flattened tree (crosses slot and shadow boundaries)
  const getVisualParent = (node) => {
    if (!node) return null;
    if (shadowDetection) {
      if (node.assignedSlot) return node.assignedSlot;
      // `instanceof ShadowRoot` is false across frames, so type + host.
      if (node.nodeType === 11 && node.host) return node.host;
    }
    return node.parentElement || node.parentNode;
  };

  let targetEl = $el;
  let finalBackground = [255, 255, 255]; // Default fallback to white

  // Allow Node.ELEMENT_NODE (1) and Node.DOCUMENT_FRAGMENT_NODE / ShadowRoot (11).
  while (targetEl && (targetEl.nodeType === 1 || targetEl.nodeType === 11)) {
    // If we land exactly on a ShadowRoot, skip computing styles and jump to its host.
    // `instanceof ShadowRoot` is false across frames, so type + host.
    if (targetEl.nodeType === 11 && targetEl.host) {
      targetEl = targetEl.host;
      continue;
    }

    const styles = Utils.getCachedStyle(targetEl);

    // Element has background image.
    const bgImage = styles.backgroundImage;
    if (bgImage && bgImage !== 'none') {
      finalBackground = { type: 'image', value: bgImage };
      break;
    }

    // Element has background colour.
    const bgColor = convertToRGBA(styles.backgroundColor);

    if (bgColor[3] !== 0 && bgColor !== 'transparent') {
      // If the background colour has an alpha channel.
      if (bgColor[3] < 1) {
        // We need to find the first non-transparent parent background and blend them together.
        let parentEl = getVisualParent(targetEl);
        let parentBgColor = 'rgba(255, 255, 255, 1)'; // Default assumption

        while (parentEl && (parentEl.nodeType === 1 || parentEl.nodeType === 11)) {
          if (parentEl.nodeType === 11 && parentEl.host) {
            parentEl = parentEl.host;
            continue;
          }

          const parentStyles = Utils.getCachedStyle(parentEl);
          const currentParentBg = parentStyles.backgroundColor;

          // Stop, valid colour found.
          if (currentParentBg !== 'rgba(0, 0, 0, 0)' && currentParentBg !== 'transparent') {
            parentBgColor = currentParentBg;
            break;
          }

          // Move up the flattened DOM tree.
          parentEl = getVisualParent(parentEl);
        }

        // If we reach the HTML tag without finding a solid background, default to white.
        if (parentBgColor === 'rgba(0, 0, 0, 0)' || parentBgColor === 'transparent') {
          parentBgColor = 'rgba(255, 255, 255, 1)';
        }

        const parentColor = convertToRGBA(parentBgColor);
        finalBackground = alphaBlend(bgColor, parentColor);
        break;
      }

      // Solid color immediately found if no alpha channel.
      finalBackground = bgColor;
      break;
    }

    // Default to white if we reach the HTML tag.
    if (targetEl.tagName === 'HTML') {
      finalBackground = [255, 255, 255];
      break;
    }

    // Move up the flattened DOM tree instead of just parentNode.
    targetEl = getVisualParent(targetEl);
  }

  // 2. Save the calculated result to our WeakMap cache before returning
  backgroundCache.set($el, finalBackground);
  return finalBackground;
}

// Garbage collection.
export function clearBackgroundCache() {
  backgroundCache = new WeakMap();
}

/**
 * Get the relative luminance of a colour based on WCAG 2.0
 * @link http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 * @param {number[]} color Colour code in [R,G,B] format.
 * @returns Luminance value.
 */
export const getLuminance = memoize(
  function getLuminance(color) {
    const rgb = color.slice(0, 3).map((x) => {
      const normalized = x / 255;
      return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  },
  (color) => color.join(','), // Key resolver: e.g., "255,255,255,1"
);

/**
 * Compute an APCA (Advanced Perceptual Contrast Algorithm) contrast value
 * between a foreground color and a background color.
 * @param {number[]} color - Foreground color in `[R, G, B, A]` format.
 * @param {number[]} bg - Background color in `[R, G, B, A]` format.
 * @returns {{ ratio: number, blendedColor: number[] }} Computed APCA value & alpha-blended foreground.
 */
export const getAPCAValue = memoize(
  function getAPCAValue(color, bg) {
    const blendedColor = alphaBlend(color, bg).slice(0, 4);
    const foreground = sRGBtoY(blendedColor);
    const background = sRGBtoY(bg);
    const ratio = APCAcontrast(foreground, background);
    return { ratio, blendedColor };
  },
  (color, bg) => `${color.join(',')}|${bg.join(',')}`,
);

/**
 * Get WCAG 2.0 contrast ratio from luminance value.
 * @link http://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
 * @param {number} l1 Luminance value of foreground colour.
 * @param {number} l2 Luminance value of background colour.
 * @returns WCAG 2.0 contrast ratio.
 */
export function getWCAG2Ratio(l1, l2) {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Brighten a foreground text colour.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {number} amount Number or increment to brighten by.
 * @returns Lighter foreground text colour.
 */
export function brighten(color, amount) {
  return color.map((value, index) => {
    if (index < 3) {
      // Only brighten [R,G,B]
      const newValue = Math.ceil(value + (255 - value) * amount);
      return newValue >= 255 ? 255 : newValue;
    }
    return value;
  });
}

/**
 * Darken a foreground text colour.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {number} amount Number or increment to darken by.
 * @returns Darker foreground text colour.
 */
export function darken(color, amount) {
  return color.map((value, index) => {
    if (index < 3) {
      // Only darken [R,G,B]
      const newValue = Math.floor(value * (1 - amount));
      return newValue <= 0 ? 0 : newValue;
    }
    return value;
  });
}

/**
 * Get the hex code equivalent of an RGB colour.
 * @param {number[]} color Colour in [R,G,B,A] format.
 * @returns Hexcode equivalent.
 */
export function getHex(color) {
  const [r, g, b] = color.map((value) => Math.min(255, Math.max(0, value)));
  const hexR = r.toString(16).padStart(2, '0');
  const hexG = g.toString(16).padStart(2, '0');
  const hexB = b.toString(16).padStart(2, '0');
  return `#${hexR}${hexG}${hexB}`;
}

/**
 * Get the display-friendly APCA contrast value for output.
 * @param {Object} value - The value object containing the contrast ratio.
 * @returns {string|number} The formatted APCA contrast value.
 */
export function displayAPCAValue(value) {
  return Math.abs(Number(value.toFixed(1)));
}

/**
 * Get the display-friendly WCAG ratio for output.
 * @param {Object} value - The value object containing the contrast ratio.
 * @returns {string|number} The formatted contrast ratio.
 */
export function displayWCAGRatio(value) {
  const truncatedRatio = Math.trunc(value * 10) / 10;
  // Round to decimal places, and display without decimals if integer.
  const formattedRatio = Number.isInteger(truncatedRatio)
    ? truncatedRatio.toFixed(0)
    : truncatedRatio;
  return `${formattedRatio}:1`;
}

/**
 * Get the display-friendly contrast value for output.
 * @param {Object} value - The value object containing the contrast ratio.
 * @param {String} contrastAlgorithm - Preferred contrast algorithm.
 * @returns {string|number} The formatted contrast ratio.
 */
export function ratioToDisplay(value, contrastAlgorithm) {
  return contrastAlgorithm === 'APCA' ? displayAPCAValue(value) : displayWCAGRatio(value);
}

/**
 * Calculate the contrast ratio or value between two colours.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {Array} bg Background colour in [R,G,B,A] format.
 * @param {String} contrastAlgorithm Preferred algorithm.
 * @returns Either WCAG 2.0 contrast ratio or APCA contrast value.
 */
export const calculateContrast = memoize(
  function calculateContrast(color, bg, contrastAlgorithm) {
    let ratio;
    const blendedColor = alphaBlend(color, bg).slice(0, 4);
    if (contrastAlgorithm === 'APCA') {
      const foreground = sRGBtoY(blendedColor);
      const background = sRGBtoY(bg);
      ratio = APCAcontrast(foreground, background);
    } else {
      // Uses WCAG 2.0 contrast algorithm based on luminance.
      const foreground = getLuminance(blendedColor);
      const background = getLuminance(bg);
      ratio = getWCAG2Ratio(foreground, background);
    }
    return { ratio, blendedColor };
  },
  (color, bg, alg) => `${color.join(',')}|${bg.join(',')}|${alg}`,
);

/**
 * Suggest a foreground colour with sufficient contrast.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {number[]} background Background colour in [R,G,B,A] format.
 * @param {boolean} isLargeText Whether text is normal or large size.
 * @param {boolean} contrastAlgorithm Preferred contrast algorithm
 * @returns Compliant colour hexcode.
 */
export const suggestColorWCAG = memoize(
  function suggestColorWCAG(color, background, isLargeText, contrastAlgorithm) {
    let minContrastRatio;
    if (contrastAlgorithm === 'AAA') {
      minContrastRatio = isLargeText ? 4.5 : 7;
    } else {
      minContrastRatio = isLargeText ? 3 : 4.5;
    }

    // Get luminance
    const fgLuminance = getLuminance(color);
    const bgLuminance = getLuminance(background);

    // Determine if text color should be lightened or darkened (considers extreme values).
    const adjustMode =
      fgLuminance > bgLuminance
        ? getWCAG2Ratio(1, bgLuminance) > minContrastRatio
        : getWCAG2Ratio(0, bgLuminance) < minContrastRatio;

    const adjustColor = (foregroundColor, amount, mode) =>
      mode ? brighten(foregroundColor, amount) : darken(foregroundColor, amount);

    let adjustedColor = color;
    let lastValidColor = adjustedColor;
    let contrastRatio = getWCAG2Ratio(fgLuminance, bgLuminance);
    let bestContrast = contrastRatio;
    let previousColor = color;

    // Loop parameters.
    let step = 0.16;
    const percentChange = 0.5;
    const precision = 0.01;
    let iterations = 0;
    const maxIterations = 100;

    while (step >= precision) {
      iterations += 1;

      // Return null if no colour found.
      if (iterations > maxIterations) {
        return { color: null };
      }

      adjustedColor = adjustColor(adjustedColor, step, adjustMode);
      const newLuminance = getLuminance(adjustedColor);
      contrastRatio = getWCAG2Ratio(newLuminance, bgLuminance);

      // console.log(`%c ${getHex(adjustedColor)} | ${contrastRatio}`, `color:${getHex(adjustedColor)};background:${getHex(background)}`);

      // Save valid colour, go back to previous, and continue with a smaller step.
      if (contrastRatio >= minContrastRatio) {
        // Ensure new colour is closer to the contrast minimum than old colour.
        lastValidColor = contrastRatio <= bestContrast ? adjustedColor : lastValidColor;
        bestContrast = contrastRatio;
        adjustedColor = previousColor;
        step *= percentChange;
      }

      previousColor = adjustedColor;
    }
    return { color: getHex(lastValidColor) };
  },
  (color, bg, isLargeText, alg) => `${color.join(',')}|${bg.join(',')}|${isLargeText}|${alg}`,
);

/**
 * Determines the optimal contrasting color (either #000 or #FFF) for a given background color and the minimum font size required to meet APCA.
 * @param {number[]} background The background color in [R, G, B, A] format.
 * @param {number} fontWeight The computed weight of the font.
 * @returns Object containing hex code (#000/#FFF) and the recommended font size.
 */
const getOptimalAPCACombo = (background, fontWeight) => {
  const contrastWithDark = getAPCAValue(background, [0, 0, 0, 1]);
  const contrastWithLight = getAPCAValue(background, [255, 255, 255, 1]);
  const isDarkBetter = Math.abs(contrastWithDark.ratio) > Math.abs(contrastWithLight.ratio);
  const suggestedColor = isDarkBetter ? [0, 0, 0, 1] : [255, 255, 255, 1];
  const bestContrastRatio = isDarkBetter ? contrastWithDark.ratio : contrastWithLight.ratio;
  const newFontLookup = fontLookupAPCA(bestContrastRatio).slice(1);
  const size = Math.ceil(newFontLookup[Math.floor(fontWeight / 100) - 1]);
  return { suggestedColor, size };
};

/**
 * Suggests a new colour or font size based on APCA contrast algorithm.
 * @param {Array[]} color Text colour in [R,G,B,A] format.
 * @param {Array[]} background Background colour in [R,G,B,A] format.
 * @param {number} fontWeight Current font weight of the element.
 * @param {number} fontSize Current font size of the element.
 * @returns Compliant colour hexcode and/or font size combination.
 */
export const suggestColorAPCA = memoize(
  function suggestColorAPCA(color, background, fontWeight, fontSize) {
    const graphicMinLc = 45;
    const isGraphic = fontWeight == null || fontSize == null;
    const bgLuminance = sRGBtoY(background);

    const adjustColor = (foregroundColor, amount) =>
      bgLuminance <= 0.179 ? brighten(foregroundColor, amount) : darken(foregroundColor, amount);

    let adjustedColor = color;
    let contrast = getAPCAValue(adjustedColor, background);
    let { ratio } = contrast;

    let bestTextCombo = null;
    let bestContrast = ratio;
    let lastValidColor = null;

    let fontLookup;
    let fontWeightIndex;
    let minimumSizeRequired;

    const passesText = () => {
      fontLookup = fontLookupAPCA(ratio).slice(1);
      fontWeightIndex = Math.min(
        Math.max(Math.floor(fontWeight / 100) - 1, 0),
        fontLookup.length - 1,
      );
      minimumSizeRequired = fontLookup[fontWeightIndex];
      return (
        minimumSizeRequired <= fontSize &&
        minimumSizeRequired !== 999 &&
        minimumSizeRequired !== 777
      );
    };

    const passesGraphic = () => Math.abs(ratio) >= graphicMinLc;

    if (!isGraphic) {
      bestTextCombo = getOptimalAPCACombo(background, fontWeight);

      // If only bigger text will work, return that suggestion.
      if (bestTextCombo.size > fontSize) {
        return {
          color: getHex(bestTextCombo.suggestedColor),
          size: bestTextCombo.size,
        };
      }

      // If current combo already passes, no need to adjust colour.
      if (passesText()) {
        return { color: getHex(color), size: null };
      }
    } else if (passesGraphic()) {
      return { color: getHex(color), size: null };
    }

    let previousColor = color;
    let step = 0.16;
    const percentChange = 0.5;
    const precision = 0.01;
    let iterations = 0;
    const maxIterations = 50;

    while (step >= precision && iterations < maxIterations) {
      iterations += 1;
      adjustedColor = adjustColor(adjustedColor, step);

      contrast = getAPCAValue(adjustedColor, background);
      ratio = contrast.ratio;

      const passes = isGraphic ? passesGraphic() : passesText();

      // console.log(`%c ${getHex(adjustedColor)} | ${displayAPCAValue(ratio)} | ${isGraphic ? `Lc≥${graphicMinLc}` : fontLookup}`, `color:${getHex(adjustedColor)};background:${getHex(background)}`);

      if (passes) {
        if (Math.abs(ratio) <= Math.abs(bestContrast) || !lastValidColor) {
          lastValidColor = adjustedColor;
          bestContrast = ratio;
        }
        adjustedColor = previousColor;
        step *= percentChange;
      }

      previousColor = adjustedColor;
    }

    if (lastValidColor) {
      return { color: getHex(lastValidColor), size: null };
    }

    if (!isGraphic && bestTextCombo) {
      return {
        color: getHex(bestTextCombo.suggestedColor),
        size: bestTextCombo.size,
      };
    }

    return { color: getHex(color), size: null };
  },
  (color, bg, weight, size) => `${color.join(',')}|${bg.join(',')}|${weight}|${size}`,
);

/**
 * Calculate an elements contrast based on WCAG 2.0 contrast algorithm.
 * @param {HTMLElement} $el The element in the DOM.
 * @param {Array} color Text colour in [R,G,B,A] format.
 * @param {Array} background Background colour in [R,G,B,A] format.
 * @param {number} fontSize Element's font size.
 * @param {number} fontWeight Element's font weight.
 * @param {number} opacity Element's opacity value.
 * @param {boolean} contrastAlgorithm Preferred contrast algorithm.
 * @returns {Object} Object containing the element, ratio, and extra details.
 */
export function wcagAlgorithm(
  $el,
  color,
  background,
  fontSize,
  fontWeight,
  opacity,
  contrastAlgorithm,
) {
  const { ratio, blendedColor } = calculateContrast(color, background);
  const isLargeText = fontSize >= 24 || (fontSize >= 18.67 && fontWeight >= 700);

  // Misc exceptions.
  const tagName = $el.tagName.toLowerCase();
  const isCloseIcon = /^[x×✕✖✗✘]$/i.test($el.textContent);
  const isCloseButton = (tagName === 'button' || tagName === 'a') && isCloseIcon;

  // Evaluate.
  let hasLowContrast;
  if (isCloseButton) {
    hasLowContrast = ratio > 0 && ratio < 3;
  } else if (contrastAlgorithm === 'AAA') {
    hasLowContrast = isLargeText ? ratio < 4.5 : ratio < 7;
  } else {
    const hasLowContrastNormalText = ratio > 0 && ratio < 4.5;
    hasLowContrast = isLargeText ? ratio < 3 : hasLowContrastNormalText;
  }

  if (hasLowContrast) {
    return {
      $el,
      ratio: displayWCAGRatio(ratio),
      color: blendedColor,
      background,
      fontSize,
      fontWeight,
      isLargeText,
      opacity,
      textUnderline: Utils.getCachedStyle($el).textDecorationLine,
    };
  }
  return null;
}

/**
 * Calculate an elements contrast based on APCA algorithm.
 * @param {HTMLElement} $el The element in the DOM.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {Array} background Background colour in [R,G,B,A] format.
 * @param {number} fontSize Element's font size.
 * @param {number} fontWeight Element's font weight.
 * @param {number} opacity Element's opacity value.
 * @returns {Object} Object containing the element, ratio, and extra details.
 */
export function apcaAlgorithm(
  $el,
  color,
  background,
  fontSize,
  fontWeight,
  opacity,
  contrastAlgorithm,
) {
  const { ratio, blendedColor } = calculateContrast(color, background, contrastAlgorithm);

  // Returns 9 font sizes in px corresponding to weights 100 thru 900.
  // Returns ['LcValue',100,200,300,400,500,600,700,800,900]
  const fontLookup = fontLookupAPCA(ratio).slice(1);

  // Get minimum font size based on weight.
  const fontWeightIndex = Math.floor(fontWeight / 100) - 1;
  const minFontSize = fontLookup[fontWeightIndex];

  if (fontSize < minFontSize) {
    return {
      $el,
      ratio: displayAPCAValue(ratio),
      color: blendedColor,
      background,
      fontWeight,
      fontSize,
      opacity,
      textUnderline: Utils.getCachedStyle($el).textDecorationLine,
    };
  }
  return null;
}

/**
 * Check an element's contrast based on APCA or WCAG 2.0 algorithm.
 * @param {HTMLElement} $el The element in the DOM.
 * @param {number[]} color Text colour in [R,G,B,A] format.
 * @param {Array} background Background colour in [R,G,B,A] format.
 * @param {number} fontSize Element's font size.
 * @param {number} fontWeight Element's font weight.
 * @param {number} opacity Element's opacity value.
 * @param {boolean} contrastAlgorithm Which algorithm to use.
 * @returns {Object} Object containing the element, ratio, and extra details.
 */
export function checkElementContrast(
  $el,
  color,
  background,
  fontSize,
  fontWeight,
  opacity,
  contrastAlgorithm,
) {
  const algorithm = contrastAlgorithm === 'APCA' ? apcaAlgorithm : wcagAlgorithm;
  return algorithm($el, color, background, fontSize, fontWeight, opacity, contrastAlgorithm);
}

/**
 * Extracts and normalizes all valid CSS color values found in a string.
 * @param {String} cssValue Usually from `background-image:`
 * @returns {Array} Returns array of RGB-converted colours.
 */
const colorTokenPattern =
  /#(?:[\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})\b|\b(?:rgb|hsl|lab|lch|oklab|oklch)a?\([^)]+\)|\b[a-z]+\b/gi;
export const extractColorFromString = memoize(
  function extractColorFromString(cssValue) {
    const tokens = cssValue.match(colorTokenPattern);
    if (!tokens) return [];
    const colors = [];
    for (const token of tokens) {
      if (/^[a-z]+$/i.test(token) && !CSS.supports('color', token)) continue;
      const color = convertToRGBA(token);
      if (color) colors.push(color);
    }
    return colors;
  },
  (cssValue) => cssValue,
);
